
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Play,
  BookOpen,
  Loader2,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { Tutorial, TutorialCategory } from '@/types/tutorials';
import { useNavigate } from 'react-router-dom';

const AdminTutorials = () => {
  const navigate = useNavigate();
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [categories, setCategories] = useState<TutorialCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTutorial, setEditingTutorial] = useState<Tutorial | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    content: string;
    video_url: string;
    video_type: 'none' | 'youtube' | 'vimeo' | 'upload';
    category_id: string;
    order_index: number;
    is_active: boolean;
  }>({
    title: '',
    description: '',
    content: '',
    video_url: '',
    video_type: 'none',
    category_id: 'no-category',
    order_index: 0,
    is_active: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('tutorial_categories')
        .select('*')
        .order('order_index', { ascending: true });

      if (categoriesError) {
        console.error('Error fetching categories:', categoriesError);
        // Continue even if categories fail
      }

      // Fetch tutorials with categories
      const { data: tutorialsData, error: tutorialsError } = await supabase
        .from('tutorials')
        .select(`
          *,
          category:tutorial_categories(*)
        `)
        .order('order_index', { ascending: true });

      if (tutorialsError) {
        console.error('Error fetching tutorials:', tutorialsError);
        toast.error('Error cargando tutoriales');
      }

      setCategories(categoriesData || []);
      setTutorials(tutorialsData || []);
    } catch (error) {
      console.error('Error in fetchData:', error);
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      content: '',
      video_url: '',
      video_type: 'none',
      category_id: 'no-category',
      order_index: 0,
      is_active: true
    });
    setEditingTutorial(null);
    setIsCreating(false);
  };

  const handleEdit = (tutorial: Tutorial) => {
    setFormData({
      title: tutorial.title,
      description: tutorial.description || '',
      content: tutorial.content,
      video_url: tutorial.video_url || '',
      video_type: tutorial.video_type,
      category_id: tutorial.category_id || 'no-category',
      order_index: tutorial.order_index,
      is_active: tutorial.is_active
    });
    setEditingTutorial(tutorial);
    setIsCreating(false);
  };

  const handleCreate = () => {
    resetForm();
    setIsCreating(true);
  };

  const handleSave = async () => {
    try {
      if (!formData.title.trim() || !formData.content.trim()) {
        toast.error('Título y contenido son obligatorios');
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Usuario no autenticado');
        return;
      }

      const tutorialData = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        content: formData.content.trim(),
        video_url: formData.video_url.trim() || null,
        video_type: formData.video_type,
        category_id: formData.category_id === 'no-category' ? null : formData.category_id,
        order_index: formData.order_index,
        is_active: formData.is_active,
        updated_at: new Date().toISOString()
      };

      if (editingTutorial) {
        // Update existing tutorial
        const { error } = await supabase
          .from('tutorials')
          .update(tutorialData)
          .eq('id', editingTutorial.id);

        if (error) throw error;
        toast.success('Tutorial actualizado correctamente');
      } else {
        // Create new tutorial
        const { error } = await supabase
          .from('tutorials')
          .insert({
            ...tutorialData,
            created_by: user.id
          });

        if (error) throw error;
        toast.success('Tutorial creado correctamente');
      }

      await fetchData();
      resetForm();
    } catch (error) {
      console.error('Error saving tutorial:', error);
      toast.error('Error guardando tutorial');
    }
  };

  const handleDelete = async (tutorialId: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este tutorial?')) return;

    try {
      const { error } = await supabase
        .from('tutorials')
        .delete()
        .eq('id', tutorialId);

      if (error) throw error;

      toast.success('Tutorial eliminado correctamente');
      await fetchData();
    } catch (error) {
      console.error('Error deleting tutorial:', error);
      toast.error('Error eliminando tutorial');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                onClick={() => navigate('/admin/dashboard')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Volver al Dashboard
              </Button>
              <div>
                <h1 className="text-3xl font-bold">Gestión de Tutoriales</h1>
                <p className="text-gray-600">Administra tutoriales y contenido educativo</p>
              </div>
            </div>
            <Button onClick={handleCreate} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Nuevo Tutorial
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {(isCreating || editingTutorial) && (
              <Card className="border-t-4 border-blue-500">
                <CardHeader>
                  <CardTitle>
                    {editingTutorial ? 'Editar Tutorial' : 'Crear Nuevo Tutorial'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Título *</label>
                      <Input
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        placeholder="Título del tutorial"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Categoría</label>
                      <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona una categoría" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no-category">Sin categoría</SelectItem>
                          {categories.map((category) => (
                            <SelectItem key={category.id} value={category.id}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Descripción</label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Breve descripción del tutorial"
                      rows={2}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Tipo de Video</label>
                      <Select value={formData.video_type} onValueChange={(value: 'none' | 'youtube' | 'vimeo' | 'upload') => setFormData({ ...formData, video_type: value })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin video</SelectItem>
                          <SelectItem value="youtube">YouTube</SelectItem>
                          <SelectItem value="vimeo">Vimeo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">URL del Video</label>
                      <Input
                        value={formData.video_url}
                        onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                        placeholder="https://..."
                        disabled={formData.video_type === 'none'}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Orden</label>
                      <Input
                        type="number"
                        value={formData.order_index}
                        onChange={(e) => setFormData({ ...formData, order_index: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Contenido *</label>
                    <Textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="Contenido del tutorial (HTML permitido)"
                      rows={8}
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                    <label className="text-sm font-medium">Tutorial activo</label>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={handleSave} className="flex items-center gap-2">
                      <Save className="h-4 w-4" />
                      Guardar
                    </Button>
                    <Button variant="outline" onClick={resetForm} className="flex items-center gap-2">
                      <X className="h-4 w-4" />
                      Cancelar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid gap-4">
              {tutorials.length === 0 ? (
                <Card>
                  <CardContent className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-600 mb-2">
                        No hay tutoriales creados
                      </h3>
                      <p className="text-gray-500 mb-4">
                        Crea tu primer tutorial para comenzar.
                      </p>
                      <Button onClick={handleCreate}>
                        Crear Tutorial
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                tutorials.map((tutorial) => (
                  <Card key={tutorial.id} className="border-l-4 border-l-green-500">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="flex items-center gap-2">
                            {tutorial.title}
                            {tutorial.video_url && tutorial.video_type !== 'none' && (
                              <Badge variant="secondary" className="flex items-center gap-1">
                                <Play className="h-3 w-3" />
                                Video
                              </Badge>
                            )}
                            {!tutorial.is_active && (
                              <Badge variant="destructive">Inactivo</Badge>
                            )}
                          </CardTitle>
                          {tutorial.description && (
                            <CardDescription>{tutorial.description}</CardDescription>
                          )}
                          {tutorial.category && (
                            <Badge variant="outline" className="mt-2">
                              {tutorial.category.name}
                            </Badge>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(tutorial)}
                            className="flex items-center gap-1"
                          >
                            <Edit className="h-4 w-4" />
                            Editar
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDelete(tutorial.id)}
                            className="flex items-center gap-1"
                          >
                            <Trash2 className="h-4 w-4" />
                            Eliminar
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm text-gray-600">
                        Orden: {tutorial.order_index} | 
                        Creado: {new Date(tutorial.created_at).toLocaleDateString()}
                        {tutorial.video_type !== 'none' && (
                          <> | Video: {tutorial.video_type}</>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTutorials;
